import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { ChatPanel } from "llmasaservice-ui";
import {
  materialDark,
  materialLight,
} from "react-syntax-highlighter/dist/esm/styles/prism";
import { Helmet } from "react-helmet";
import { useQuery } from "@tanstack/react-query";
import { API } from "aws-amplify";
import { LLMEmailCaptureMode } from "../API";

const EmbedAgentComponent: React.FC = () => {
  const [followOnPrompt, setFollowOnPrompt] = useState<string>("");
  const [followOnQuestions, setFollowOnQuestions] = useState<string[] | null>(
    null
  );
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id") || "";
  const customer_id = searchParams.get("customer_id") || "";
  const customer_email = searchParams.get("customer_email") || "";

  const {
    data: agentData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["agents", { id: id }],
    queryFn: () => API.get("llmpublicapi", `/agents/${id}`, {}),
    enabled: !!id,
  });

  if (id && isLoading) return <div>Loading...</div>;

  if (id && isError)
    return <div>Error loading agent - {error ? error.message : ""}</div>;

  const agent = agentData ? agentData[0] : null;

  const extractValue = (
    match: string,
    groups: any[] = [],
    extraArgs: string[] = []
  ): string => {
    // Rule 1: If there are no extraArgs and no groups, use match.
    if (
      (!extraArgs || extraArgs.length === 0) &&
      (!groups || groups.length === 0)
    ) {
      return match;
    }
    // Rule 2: If there are no extraArgs but groups exist, use groups[0].
    if ((!extraArgs || extraArgs.length === 0) && groups && groups.length > 0) {
      return groups[0];
    }
    // Rule 3: If there are extraArgs, use the first one as a template.
    if (extraArgs && extraArgs.length > 0) {
      const template = extraArgs[0];
      return template.replace(/\$(\d+)/g, (_, index) => {
        const i = parseInt(index, 10);
        return groups[i] !== undefined ? groups[i] : "";
      });
    }
    return "";
  };

  const openUrlActionCallback = (
    match: string,
    groups: any[],
    ...extraArgs: string[]
  ) => {
    const url = extractValue(match, groups, extraArgs);
    if (url && (url.startsWith("http") || url.startsWith("mailto"))) {
      window.open(url, "_blank");
    }
  };

  const copyToClipboardCallback = (
    match: string,
    groups: any[],
    ...extraArgs: string[]
  ) => {
    const value = extractValue(match, groups, extraArgs);
    navigator.clipboard.writeText(value);
  };

  const showAlertCallback = (
    match: string,
    groups: any[],
    ...extraArgs: string[]
  ) => {
    console.log("showAlertCallback", match, groups, extraArgs);
    const value = extractValue(match, groups, extraArgs);
    alert(value);
  };

  const sendFollowOnPromptCallback = (
    match: string,
    groups: any[],
    ...extraArgs: string[]
  ) => {
    const value = extractValue(match, groups, extraArgs);
    setFollowOnPrompt(value);
  };

  const setFollowUpQuestionsCallback = (
    match: string,
    groups: any[],
    ...extraArgs: string[]
  ) => {
    const value = extractValue(match, groups, extraArgs);
    setFollowOnQuestions(value.split("|"));
  };

  const openIframeCallback = (
    match: string,
    groups: any[],
    ...extraArgs: string[]
  ) => {
    const url = extractValue(match, groups, extraArgs);
    console.log("openIframeCallback", match, groups, extraArgs, url);
    // Update state to render an iframe if the url is valid.
    if (url && (url.startsWith("http") || url.startsWith("https"))) {
      setIframeUrl(url);
    }
  };

  // Create a mapping of callback names to function references
  const callbackMapping: {
    [key: string]: (match: string, groups: any[], ...args: string[]) => void;
  } = {
    openUrlActionCallback,
    copyToClipboardCallback,
    showAlertCallback,
    sendFollowOnPromptCallback,
    setFollowUpQuestionsCallback,
    openIframeCallback,
  };

  const actionsParam = searchParams.get("actions");
  let actions: any[] = [];
  if (actionsParam) {
    try {
      const decodedActionsParam = decodeURIComponent(actionsParam);
      actions = JSON.parse(decodedActionsParam);
      if (!Array.isArray(actions)) {
        throw new Error("Parsed actions is not an array");
      }

      actions = actions.map((action) => {
        if (
          typeof action.callback === "string" &&
          callbackMapping[action.callback]
        ) {
          return {
            ...action,
            callback: callbackMapping[action.callback],
          };
        }
        return action;
      });
    } catch (error) {
      console.error("Failed to parse actions:", error, actionsParam);
      actions = [];
    }
  }

  let url = "https://chat.llmasaservice.io/";
  // if the url is localhost or dev.llmasaservice.io, we are in development mode and we should use the dev endpoint
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "dev.llmasaservice.io"
  ) {
    url = "https://chat.llmasaservice.io/dev";
  }

  // Helper to parse callback strings like "sendFollowOnPromptCallback(arg1, arg2)"
  const parseCallbackString = (callbackStr: string) => {
    const regex = /^(\w+)(?:\((.+)\))?$/;
    const match = callbackStr.match(regex);
    if (match) {
      const name = match[1];
      // If there are args, split by comma and trim whitespace.
      const args = match[2] ? match[2].split(",").map((arg) => arg.trim()) : [];
      return { name, args };
    }
    return null;
  };

  const getActionsArraySafely = (actionsString: string) => {
    console.log("getActionsArraySafely", actionsString);
    let actions: any[] = [];
    if (actionsString && actionsString !== "") {
      try {
        actions = JSON.parse(actionsString);
        if (!Array.isArray(actions)) {
          throw new Error("Parsed actions is not an array");
        }
        // Map string callbacks to actual functions using callbackMapping and parsing args if needed
        actions = actions
          .map((action) => {
            if (typeof action.callback === "string") {
              const parsed = parseCallbackString(action.callback);
              if (parsed && callbackMapping[parsed.name]) {
                // Wrap the callback so that it receives the original match & groups plus extra args
                return {
                  ...action,
                  callback: (match: string, groups: any[]) =>
                    callbackMapping[parsed.name](match, groups, ...parsed.args),
                };
              } else {
                console.warn("Invalid or missing callback in action:", action);
                // Optionally provide a no-op fallback or skip the action:
                return null;
              }
            } else {
              return action;
            }
          })
          .filter(Boolean); // removes null entries
      } catch (error) {
        console.error("Error parsing actions string:", error);
        actions = [];
      }
    }

    console.log("getActionsArraySafely actions", actions);
    return actions;
  };

  return (
    <>
      <Helmet>
        <base target="_blank" />
        <link
          rel="iframely reader resizable"
          type="text/html"
          href={`https://app.llmasaservice.io/embedagent?id=${id}&customer_id=&customer_email=`}
          media="height: 600"
        />
      </Helmet>

      {agent && (
        <ChatPanel
          project_id={agent?.projectId || ""}
          service={agent?.groupId || null}
          url={url}
          title={agent?.displayTitle ?? "Chat Agent"}
          theme={agent?.displayTheme === "light" ? "light" : "dark"}
          cssUrl={agent?.cssUrl ?? ""}
          height={agent?.displayHeight ?? "75vh"}
          width={agent?.displayWidth ?? "100%"}
          promptTemplate={agent?.displayPromptTemplate ?? "{{prompt}}"}
          initialMessage={
            agent?.displayStartMessageOrPrompt === "message"
              ? agent?.displayInitialMessageOrPrompt ?? ""
              : undefined
          }
          initialPrompt={
            agent?.displayStartMessageOrPrompt === "prompt"
              ? agent?.displayInitialMessageOrPrompt ?? ""
              : undefined
          }
          followOnQuestions={
            followOnQuestions
              ? followOnQuestions
              : agent?.displayFollowOnPrompts?.split("|") ?? []
          }
          historyChangedCallback={(history) => {
            if (history) {
              setFollowOnPrompt("");
            }
          }}
          prismStyle={
            (agent?.displayTheme === "light"
              ? materialLight
              : materialDark) as any
          }
          actions={getActionsArraySafely(agent?.displayActions ?? "")}
          followOnPrompt={followOnPrompt}
          agent={id ?? null}
          placeholder={agent?.displayPlaceholder ?? "Type a message"}
          hideInitialPrompt={agent?.displayHideInitialPrompt ?? true}
          data={agent?.data ? [{ key: "data", data: agent?.data }] : []}
          showEmailButton={agent?.displayShowEmailButton ?? true}
          showSaveButton={agent?.displayShowSaveButton ?? true}
          showCallToAction={agent?.displayShowCallToAction ?? false}
          callToActionButtonText={
            agent?.displayCallToActionButtonText ?? "Submit"
          }
          callToActionEmailAddress={
            agent?.displayCallToActionEmailAddress ?? ""
          }
          callToActionEmailSubject={
            agent?.displayCallToActionEmailSubject ?? "Agent CTA Submitted"
          }
          customer={{
            customer_id: customer_id ?? "default",
            customer_user_email: customer_email ?? "",
          }}
          scrollToEnd={agent?.displayScrollToEnd ?? false}
          createConversationOnFirstChat={
            agent?.createConversationOnFirstChat ?? true
          }
          customerEmailCaptureMode={
            agent?.customerEmailCaptureMode ?? LLMEmailCaptureMode.HIDE
          }
          customerEmailCapturePlaceholder={
            agent?.customerEmailCapturePlaceholder ??
            "Please enter your email..."
          }
          responseCompleteCallback={(callId, prompt, response) => {
            if (!agent?.displayActions) return;

            // Get the actions array from the agent's displayActions
            const actionsArray = getActionsArraySafely(
              agent?.displayActions ?? ""
            );

            // Loop through each action and if it's of type "response" and has a pattern,
            // test the response against the pattern.
            actionsArray.forEach((action) => {
              if (action.type === "response" && action.pattern) {
                // Create a regex from the action's pattern.
                const regex = new RegExp(action.pattern, "gi");
                const matches = regex.exec(response);
                console.log("Regex matches:", matches);
                if (matches) {
                  // matches[0] contains the full match, and matches.slice(1) the groups.
                  // Call the action's callback using your three-rule algorithm.
                  action.callback(matches[0], matches.slice(1));
                }
              }
            });
          }}
        />
      )}
    {/* Modal with iframe */}
    {iframeUrl && (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
        }}
      >
        <div style={{ position: "relative", width: "95vw", height: "95vh" }}>
          <button
            onClick={() => setIframeUrl(null)}
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 10000,
              background: "#fff",
              border: "none",
              padding: "5px 5px",
              cursor: "pointer",
            }}
          >
            Close
          </button>
          <iframe
            src={iframeUrl}
            title="Dynamic Iframe"
            style={{ width: "100%", height: "100%", border: "none" }}
          />
        </div>
      </div>
    )}

    </>
  );
};

export default EmbedAgentComponent;
